import { 
  User, 
  CreateUserRequest, 
  UpdateUserRequest, 
  ApiResponse, 
  PaginatedResponse 
} from '../types/user';

// Re-export the interfaces for backward compatibility
export type { CreateUserRequest as CreateUserData, UpdateUserRequest as UpdateUserData };
import API_CONFIG from '../config/api.config';
import httpClient from './httpClient';

const USERS_API_URL = API_CONFIG.ENDPOINTS.USERS;

// Response types
interface UserResponse extends ApiResponse<User> {}

// Error handling
class ApiError extends Error {
  statusCode?: number;
  
  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

export const userApi = {
  /**
   * Fetches a paginated list of users
   * @param page - Page number (1-based)
   * @param limit - Number of items per page
   * @param search - Optional search query
   * @param filters - Additional filters
   */
  async getUsers(
    page: number = 1,
    limit: number = 1000, // Increased default limit to get all users
    search?: string,
    filters?: Record<string, any>
  ): Promise<PaginatedResponse<User>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { q: search }),
        ...filters,
      });
      
      console.log('Fetching users from:', `${USERS_API_URL}?${params}`);
      const response = await httpClient.get<User[] | { data: User[] }>(
        USERS_API_URL
      );
      
      console.log('Raw API response:', response);
      
      // Handle both response formats:
      // 1. Direct array of users
      if (Array.isArray(response.data)) {
        console.log('Received direct array of users');
        return {
          items: response.data,
          total: response.data.length,
          page: 1,
          limit: response.data.length,
          totalPages: 1,
          hasNextPage: false,
          hasPreviousPage: false,
        };
      }
      
      // 2. Response with data property containing users array
      if (response.data && Array.isArray((response.data as any).data)) {
        console.log('Received paginated response');
        const data = response.data as any;
        return {
          items: data.data || [],
          total: data.total || data.data?.length || 0,
          page: data.page || 1,
          limit: data.limit || data.data?.length || 10,
          totalPages: data.totalPages || 1,
          hasNextPage: data.hasNextPage || false,
          hasPreviousPage: data.hasPreviousPage || false,
        };
      }
      
      // If we get here, the response format is unexpected
      console.error('Unexpected response format:', response.data);
      return {
        items: [],
        total: 0,
        page,
        limit,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: page > 1,
      };
    } catch (error: any) {
      console.error('Error fetching users:', error);
      throw new ApiError(
        error.response?.data?.message || error.message || 'Failed to fetch users',
        error.response?.status
      );
    }
  },
  
  // Get all users (legacy, consider using getUsers with pagination)
  async getAllUsers(): Promise<User[]> {
    const response = await this.getUsers(1, 1000);
    return response.items;
  },

  /**
   * Fetches a user by ID
   */
  async getUser(id: string): Promise<User> {
    try {
      const response = await httpClient.get<UserResponse>(`${USERS_API_URL}/${id}`);
      
      if (!response.data.success || !response.data.data) {
        throw new ApiError(response.data.error || 'User not found', 404);
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error(`Error fetching user ${id}:`, error);
      throw new ApiError(
        error.response?.data?.message || error.message || 'Failed to fetch user',
        error.response?.status
      );
    }
  },

  /**
   * Creates a new user
   */
  async createUser(userData: CreateUserRequest): Promise<User> {
    try {
      const response = await httpClient.post<UserResponse>(USERS_API_URL, userData);
      
      if (!response.data.success || !response.data.data) {
        throw new ApiError(response.data.error || 'Failed to create user');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('Error creating user:', error);
      throw new ApiError(
        error.response?.data?.message || error.message || 'Failed to create user',
        error.response?.status
      );
    }
  },

  /**
   * Updates an existing user
   */
  async updateUser(id: string, userData: UpdateUserRequest): Promise<User> {
    try {
      // Filter out undefined values to avoid sending them to the server
      const payload = Object.fromEntries(
        Object.entries(userData).filter(([_, value]) => value !== undefined)
      );
      
      const response = await httpClient.put<UserResponse>(
        `${USERS_API_URL}/${id}`,
        payload
      );
      
      if (!response.data.success || !response.data.data) {
        throw new ApiError(response.data.error || 'Failed to update user');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error(`Error updating user ${id}:`, error);
      throw new ApiError(
        error.response?.data?.message || error.message || 'Failed to update user',
        error.response?.status
      );
    }
  },

  /**
   * Deletes a user by ID
   */
  async deleteUser(id: string): Promise<boolean> {
    try {
      const response = await httpClient.delete<ApiResponse<null>>(`${USERS_API_URL}/${id}`);
      
      if (!response.data.success) {
        throw new ApiError(response.data.error || 'Failed to delete user');
      }
      
      return true;
    } catch (error: any) {
      console.error(`Error deleting user ${id}:`, error);
      throw new ApiError(
        error.response?.data?.message || error.message || 'Failed to delete user',
        error.response?.status
      );
    }
  },
  
  /**
   * Requests a password reset for a user
   */
  async requestPasswordReset(email: string): Promise<boolean> {
    try {
      const response = await httpClient.post<ApiResponse<null>>(
        `${USERS_API_URL}/request-password-reset`,
        { email }
      );
      
      if (!response.data.success) {
        throw new ApiError(response.data.error || 'Failed to request password reset');
      }
      
      return true;
    } catch (error: any) {
      console.error('Error requesting password reset:', error);
      throw new ApiError(
        error.response?.data?.message || error.message || 'Failed to request password reset',
        error.response?.status
      );
    }
  },
  
  /**
   * Resets a user's password using a reset token
   */
  async resetPasswordWithToken(
    token: string, 
    newPassword: string
  ): Promise<boolean> {
    try {
      const response = await httpClient.post<ApiResponse<null>>(
        `${USERS_API_URL}/reset-password`,
        { token, newPassword }
      );
      
      if (!response.data.success) {
        throw new ApiError(response.data.error || 'Failed to reset password');
      }
      
      return true;
    } catch (error: any) {
      console.error('Error resetting password:', error);
      throw new ApiError(
        error.response?.data?.message || error.message || 'Failed to reset password',
        error.response?.status
      );
    }
  },

  /**
   * Resets a user's password (admin function)
   */
  async resetUserPassword(id: string, newPassword: string): Promise<boolean> {
    try {
      const response = await httpClient.post<ApiResponse<null>>(
        `${USERS_API_URL}/${id}/reset-password`,
        { newPassword }
      );
      
      if (!response.data.success) {
        throw new ApiError(response.data.error || 'Failed to reset user password');
      }
      
      return true;
    } catch (error: any) {
      console.error(`Error resetting password for user ${id}:`, error);
      throw new ApiError(
        error.response?.data?.message || error.message || 'Failed to reset user password',
        error.response?.status
      );
    }
  }
};
